<script setup lang="ts">
  import { useModal } from 'vue-final-modal';
  // Composables
  import { useFileUploadStore } from '@/features/Document';
  // Components
  import { IconImage, ConfirmDeleteModal } from '@/shared/ui';
  // Icons
  import UploadSuccessIcon from '@/shared/assets/icons/UploadSuccessIcon.svg';
  import PdfFileIcon from '@/shared/assets/icons/PdfFileIcon.svg';
  import TrashIcon from '@/shared/assets/icons/GrayTrashIcon.svg';
  // Types
  import type { FileStatus } from '../model/upload/types';

  const props = defineProps<{
    file: File;
    getFileProgress: (file: File) => number;
    getFileStatus: (file: File) => FileStatus;
    documentId?: number;
    filiais?: boolean;
    hideDeleteButton?: boolean;
  }>();

  const fileUploadStore = useFileUploadStore();
  const { removeFile } = fileUploadStore;

  const emit = defineEmits<{
    (e: 'fileDeleted', documentId: number): void;
    (e: 'imageDeleted', file: File): void;
  }>();

  const { open: openDelete, close: closeDelete } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title: props.filiais
        ? 'Tem certeza que deseja deletar a logo?'
        : 'Tem certeza que deseja deletar o Documento?',
      description: 'A ação não pode ser desfeita.',
      onClose: () => {
        closeDelete();
      },
      onConfirm: async () => {
        if (!props.filiais) {
          removeFile(props.file);
        } else {
          emit('imageDeleted', props.file);
        }
        closeDelete();
      },
    },
  });

  const handleDelete = () => {
    openDelete();
  };
</script>

<template>
  <div class="flex items-center justify-between">
    <div class="flex items-center w-1/4 gap-1.5 mr-20">
      <IconImage
        :src="PdfFileIcon"
        alt="Document"
        class="mr-2"
      />
      <span class="text-sm text-gray-07 text-clip break-all overflow-hidden">{{ file.name }}</span>
    </div>
    <div class="relative w-1/2 h-2 bg-gray-02 rounded-full overflow-hidden flex-shrink-0">
      <div
        class="absolute top-0 left-0 h-full transition-all duration-300 rounded-full"
        :class="{
          'bg-[var(--theme-secondary)]': getFileProgress(file) < 100,
          'bg-green-19': getFileProgress(file) === 100,
        }"
        :style="{ width: `${getFileProgress(file)}%` }"
      />
    </div>
    <span class="ml-2 text-xs text-gray-07 min-w-[40px] text-right">
      {{ getFileProgress(file) }}%
    </span>
    <span class="ml-2 text-green-19 w-1/12 flex-shrink-0">
      <IconImage
        v-show="getFileStatus(file) === 'completed'"
        :src="UploadSuccessIcon"
        alt="Upload Success"
        class="w-5 h-5 m-auto"
      />
    </span>
    <span
      v-show="!hideDeleteButton"
      class="flex-shrink-0 text-gray-xx-02"
    >
      <IconImage
        :src="TrashIcon"
        alt="Trash"
        class="w-5 h-5 cursor-pointer"
        @click="handleDelete"
      />
    </span>
  </div>
</template>
